/**
 * lifecycle.ts — Centralized Browser Lifecycle Signal Bus
 *
 * Single source of truth for all browser lifecycle events.
 * Consumers subscribe to typed signals instead of attaching
 * their own window/document listeners.
 *
 * Signals:
 *   APP_BACKGROUND    — tab hidden (visibilitychange → hidden)
 *   APP_FOREGROUND    — tab visible (visibilitychange → visible)
 *   NETWORK_OFFLINE   — navigator.onLine → false
 *   NETWORK_ONLINE    — navigator.onLine → true
 *   BACKEND_UNAVAILABLE — health check failed (set by API layer)
 *   BACKEND_RESTORED    — health check recovered (set by API layer)
 *   PAGE_UNLOADING    — beforeunload / pagehide fired
 *
 * Data-only module — no React imports.
 */

// ─────────────────────────────────────────────────────────────
// Signal Types
// ─────────────────────────────────────────────────────────────

export const LifecycleSignal = {
  APP_BACKGROUND: "APP_BACKGROUND",
  APP_FOREGROUND: "APP_FOREGROUND",
  NETWORK_OFFLINE: "NETWORK_OFFLINE",
  NETWORK_ONLINE: "NETWORK_ONLINE",
  BACKEND_UNAVAILABLE: "BACKEND_UNAVAILABLE",
  BACKEND_RESTORED: "BACKEND_RESTORED",
  PAGE_UNLOADING: "PAGE_UNLOADING",
} as const;

export type LifecycleSignal =
  (typeof LifecycleSignal)[keyof typeof LifecycleSignal];

// ─────────────────────────────────────────────────────────────
// State
// ─────────────────────────────────────────────────────────────

export interface LifecycleState {
  /** App is in foreground (tab visible) */
  isVisible: boolean;
  /** Browser reports network available */
  isOnline: boolean;
  /** Backend health check passed recently */
  isBackendUp: boolean;
  /** Page is unloading (beforeunload/pagehide fired) */
  isUnloading: boolean;
  /** Timestamp of last signal */
  lastSignalTs: number;
  /** Last signal emitted */
  lastSignal: LifecycleSignal | null;
}

// ─────────────────────────────────────────────────────────────
// Listener type
// ─────────────────────────────────────────────────────────────

type LifecycleListener = (
  signal: LifecycleSignal,
  state: Readonly<LifecycleState>,
) => void;

// ─────────────────────────────────────────────────────────────
// Singleton Bus
// ─────────────────────────────────────────────────────────────

class LifecycleBus {
  private _state: LifecycleState;
  private _listeners: Set<LifecycleListener> = new Set();
  private _started = false;

  constructor() {
    this._state = {
      isVisible: typeof document !== "undefined"
        ? document.visibilityState === "visible"
        : true,
      isOnline: typeof navigator !== "undefined"
        ? navigator.onLine
        : true,
      isBackendUp: true, // assume up until proven otherwise
      isUnloading: false,
      lastSignalTs: Date.now(),
      lastSignal: null,
    };
  }

  /** Read-only snapshot of current state */
  get state(): Readonly<LifecycleState> {
    return this._state;
  }

  /** Subscribe to all lifecycle signals. Returns unsubscribe function. */
  subscribe(listener: LifecycleListener): () => void {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  /**
   * Emit a signal from outside the bus (e.g., API layer reports backend down).
   * Only BACKEND_UNAVAILABLE and BACKEND_RESTORED should be emitted externally.
   */
  emit(signal: typeof LifecycleSignal.BACKEND_UNAVAILABLE | typeof LifecycleSignal.BACKEND_RESTORED): void {
    if (signal === LifecycleSignal.BACKEND_UNAVAILABLE) {
      if (!this._state.isBackendUp) return; // already known
      this._state.isBackendUp = false;
    } else if (signal === LifecycleSignal.BACKEND_RESTORED) {
      if (this._state.isBackendUp) return; // already known
      this._state.isBackendUp = true;
    }
    this._broadcast(signal);
  }

  /** Start listening to browser events. Call once at app boot. */
  start(): void {
    if (this._started) return;
    this._started = true;

    if (typeof document === "undefined") return; // SSR guard

    // ── Visibility ──
    document.addEventListener("visibilitychange", this._onVisibility);

    // ── Network ──
    window.addEventListener("online", this._onOnline);
    window.addEventListener("offline", this._onOffline);

    // ── Unload ──
    // pagehide is more reliable than beforeunload on mobile
    window.addEventListener("pagehide", this._onUnload);
    window.addEventListener("beforeunload", this._onUnload);

    console.log(
      "[Lifecycle] Bus started |",
      `visible=${this._state.isVisible}`,
      `online=${this._state.isOnline}`,
    );
  }

  /** Stop listening. For tests / cleanup. */
  stop(): void {
    if (!this._started) return;
    this._started = false;

    document.removeEventListener("visibilitychange", this._onVisibility);
    window.removeEventListener("online", this._onOnline);
    window.removeEventListener("offline", this._onOffline);
    window.removeEventListener("pagehide", this._onUnload);
    window.removeEventListener("beforeunload", this._onUnload);
  }

  // ── Internal Handlers ──

  private _onVisibility = (): void => {
    const visible = document.visibilityState === "visible";
    if (this._state.isVisible === visible) return; // no change
    this._state.isVisible = visible;
    this._broadcast(
      visible
        ? LifecycleSignal.APP_FOREGROUND
        : LifecycleSignal.APP_BACKGROUND,
    );
  };

  private _onOnline = (): void => {
    if (this._state.isOnline) return;
    this._state.isOnline = true;
    this._broadcast(LifecycleSignal.NETWORK_ONLINE);
  };

  private _onOffline = (): void => {
    if (!this._state.isOnline) return;
    this._state.isOnline = false;
    this._broadcast(LifecycleSignal.NETWORK_OFFLINE);
  };

  private _onUnload = (): void => {
    if (this._state.isUnloading) return; // dedupe pagehide + beforeunload
    this._state.isUnloading = true;
    this._broadcast(LifecycleSignal.PAGE_UNLOADING);
  };

  // ── Broadcast ──

  private _broadcast(signal: LifecycleSignal): void {
    this._state.lastSignalTs = Date.now();
    this._state.lastSignal = signal;

    console.log(`[Lifecycle] ${signal} | online=${this._state.isOnline} visible=${this._state.isVisible} backend=${this._state.isBackendUp}`);

    for (const listener of this._listeners) {
      try {
        listener(signal, this._state);
      } catch (err) {
        console.error("[Lifecycle] Listener error:", err);
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────
// Singleton access
// ─────────────────────────────────────────────────────────────

let _instance: LifecycleBus | null = null;

export function getLifecycleBus(): LifecycleBus {
  if (!_instance) {
    _instance = new LifecycleBus();
  }
  return _instance;
}

/**
 * Convenience: check if app is in a state where side effects should run.
 * False if backgrounded, offline, or unloading.
 */
export function isAppActive(): boolean {
  const s = getLifecycleBus().state;
  return s.isVisible && s.isOnline && !s.isUnloading;
}

/**
 * Convenience: check if backend is reachable.
 */
export function isBackendReachable(): boolean {
  const s = getLifecycleBus().state;
  return s.isOnline && s.isBackendUp && !s.isUnloading;
}
